import React from "react";

import { AdminSection, Button, Col, Form, Icon, Input, InputNumber, Option, Row, Select, Typography } from "components";

export const MethodPoTab = ({
  addQuestion,
  cropFactors,
  deleteCropFactor,
  deleteQuestion,
  method,
  moveQuestionDown,
  moveQuestionUp,
  questions,
  t,
  updateCropFactors,
  updatePlaceholder,
  updateQuestion,
  updateQuestionOptions,
  updateQuestionOptional,
  updateQuestionSf,
  updateQuestionType,
}) => {
  const fieldCropsEnum = t("enums:fieldCrops", { returnObjects: true });

  return (
    <>
      <AdminSection
        headline="Onboarding points"
        description={
          <>
            Modify the onboarding points (P<sub>o</sub>) for all users by applying an S<sub>o</sub> factor. If S<sub>o</sub> is zero, this section will be
            ignored (except for F).
          </>
        }
      />
      <Row>
        <Col sm={4} md={4}>
          <InputNumber name="so" label="So" rules={[{ required: true, type: "number" }]} initialValue={method && method.so} className="no-suffix" min={0} />
        </Col>
      </Row>
      <AdminSection
        headline="Crop factors"
        description={<>Modify the points received by adding a factor (F) based on the crop for the year that the method is added.</>}
      />
      {cropFactors.map((cropFactor, i) => (
        <div key={i}>
          {cropFactor && (
            <Row>
              <Col sm={12} md={12}>
                <Select
                  name={`cropFactors-${i}`}
                  label="Crop"
                  rules={[{ required: false }]}
                  initialValue={cropFactor.crop}
                  onChange={(v) => updateCropFactors(i, v, "crop")}
                  popupMatchSelectWidth={true}
                >
                  {Object.keys(fieldCropsEnum).map((item, k) => (
                    <Option value={fieldCropsEnum[item]} key={k}>
                      {fieldCropsEnum[item]}
                    </Option>
                  ))}
                </Select>
              </Col>
              <Col sm={8} md={8}>
                <InputNumber
                  name={`factor-${i}`}
                  label="F"
                  rules={[{ required: false, type: "number" }]}
                  className="no-suffix"
                  initialValue={cropFactor.factor ? parseFloat(cropFactor.factor) : undefined}
                  onBlur={(v) => updateCropFactors(i, v.target.value, "factor")}
                />
              </Col>
              <Col sm={4} md={4}>
                {i !== cropFactors.length - 1 && <Icon iconName="tabler/trash" className="icon-btn margin-top-75" onClick={() => deleteCropFactor(i)} />}
              </Col>
            </Row>
          )}
        </div>
      ))}
      <AdminSection
        headline="Onboarding questions"
        description={
          <>
            Add any number of questions for users when adding a method. Important: S<sub>f</sub> is only applied for select and multi-select fields. Placeholder
            is only used for input, select and date fields (if you exclude a placeholder, then the default will be used).
          </>
        }
      />
      {questions.map((item, i) => (
        <div key={i}>
          {questions[i] && (item.section === "onboarding" || !item.section) && (
            <div className="margin-bottom-200">
              <Row>
                <Col sm={12} md={12}>
                  <Typography variant="paragraph-m">
                    <b>Question {questions[i].count}</b>
                  </Typography>
                </Col>
                <Col sm={12} md={12} className="right">
                  <Icon iconName="tabler/trash" className="icon-btn" onClick={() => deleteQuestion(i)} />
                  {questions[i].count > 1 && <Icon iconName="tabler/arrow-up" className="icon-btn" onClick={() => moveQuestionUp(i)} />}
                  {questions[i].count < questions[questions.length - 1].count && questions[questions.length - 1].count > 1 && (
                    <Icon iconName="tabler/arrow-down" className="icon-btn" onClick={() => moveQuestionDown(i)} />
                  )}
                </Col>
              </Row>
              <Row>
                <Col>
                  <Input
                    name={`question-${i}`}
                    label={"Question"}
                    onBlur={(v) => updateQuestion(i, v)}
                    rules={[{ required: true }]}
                    initialValue={questions[i].question}
                  />
                </Col>
              </Row>
              <Row>
                <Col sm={18} md={18}>
                  <Input
                    name={`placeholder-${i}`}
                    rules={[{ required: false }]}
                    initialValue={questions[i].placeholder}
                    label={"Placeholder"}
                    onBlur={(v) => updatePlaceholder(i, v)}
                  />
                </Col>
                <Col sm={6} md={6}>
                  <Select
                    name={`optional-${i}`}
                    label="Optional"
                    rules={[{ required: true }]}
                    initialValue={questions[i].optional}
                    onChange={(v) => updateQuestionOptional(i, v)}
                  >
                    <Option value={false}>Required</Option>
                    <Option value={true}>Not required</Option>
                  </Select>
                </Col>
              </Row>
              <Row>
                <Col sm={8} md={8}>
                  <Select
                    name={`type-${i}`}
                    label="Type"
                    rules={[{ required: true }]}
                    initialValue={questions[i].type}
                    onChange={(v) => updateQuestionType(i, v)}
                  >
                    <Option value="input">Input</Option>
                    <Option value="number">Number input</Option>
                    <Option value="select">Select</Option>
                    <Option value="multi">Multi select</Option>
                    <Option value="date">Date</Option>
                    <Option value="image">Image</Option>
                  </Select>
                </Col>
                {(questions[i].type === "select" || questions[i].type === "multi") && (
                  <Col sm={16} md={16}>
                    <Select
                      name={`options-${i}`}
                      label="Enter options"
                      rules={[{ required: true }]}
                      initialValue={questions[i].values}
                      mode="tags"
                      style={{ width: "100%" }}
                      onChange={(v) => updateQuestionOptions(i, v)}
                    >
                      <Option value="Yes">Yes</Option>
                      <Option value="No">No</Option>
                    </Select>
                  </Col>
                )}
              </Row>
              {questions[i].type === "select" && (
                <>
                  {questions[i].values.map((item, k) => (
                    <Row key={k}>
                      <Col sm={8} md={8}>
                        <InputNumber
                          name={`sf-${i}-${k}`}
                          label="Sf"
                          onBlur={(v) => updateQuestionSf(i, k, v)}
                          min={0}
                          rules={[{ required: true, type: "number" }]}
                          className="no-suffix"
                          initialValue={questions[i].sf[k] ? parseFloat(questions[i].sf[k]) : undefined}
                        />
                      </Col>
                      <Col sm={16} md={16}>
                        <Typography variant="paragraph-m" style={{ margin: "1rem 0 0 0" }}>
                          For option <b>{item}</b>
                        </Typography>
                      </Col>
                    </Row>
                  ))}
                </>
              )}
            </div>
          )}
        </div>
      ))}
      <Row>
        <Col>
          <Form.Item>
            <Button onClick={() => addQuestion("onboarding")}>Add a question</Button>
          </Form.Item>
        </Col>
      </Row>
    </>
  );
};
